import React, { useState, useEffect } from 'react';

const CourseSidebar = ({
  course,
  availableSections,
  onClose,
  onLockSection,
  scheduleCombinations,
  onSelectAlternative
}) => {
  const [activeTab, setActiveTab] = useState("details");
  const [isVisible, setIsVisible] = useState(false);

  // Trigger slide-in animation when the sidebar mounts.
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Compute detailsInfo just like in CourseSection:
  let detailsInfo;
  if (course.composite) {
    console.log("Composite course in sidebar:", course);
    detailsInfo = course.sfuData?.info || {};
    // Fallback: if description is missing, try merging again.
    if (!detailsInfo.description && course.lecture && course.tutorial) {
      detailsInfo.description = `${(course.lecture.sfuData?.info?.description || "").trim()} ${(course.tutorial.sfuData?.info?.description || "").trim()}`.trim();
      console.log("Fallback merged description:", detailsInfo.description);
    }
  } else {
    detailsInfo = course.sfuData?.info || course.lecture?.sfuData?.info || course.tutorial?.sfuData?.info;
  }  

  // Compute alternatives by returning all schedule combinations that include this course.
  const alternatives = scheduleCombinations
    ? scheduleCombinations.filter(combo =>
        combo.combination.some(opt => opt.dept === course.dept && opt.number === course.number)
      )
    : [];

  // Utility functions for color interpolation.
  const hexToRgb = (hex) => {
    hex = hex.replace(/^#/, "");
    if (hex.length === 3) {
      hex = hex.split("").map((c) => c + c).join("");
    }
    const intVal = parseInt(hex, 16);
    return [(intVal >> 16) & 255, (intVal >> 8) & 255, intVal & 255];
  };

  const rgbToHex = (r, g, b) => {
    const toHex = (c) => {
      const hex = c.toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    };
    return "#" + toHex(r) + toHex(g) + toHex(b);
  };

  const interpolateColor = (color1, color2, factor) => {
    const rgb1 = hexToRgb(color1);
    const rgb2 = hexToRgb(color2);
    const result = rgb1.map((c, i) => Math.round(c + factor * (rgb2[i] - c)));
    return rgbToHex(...result);
  };

  const getColorsForScore = (score) => {
    const redBg = "#fee2e2";
    const yellowBg = "#fef9c3";
    const greenBg = "#d1fae5";
    const redText = "#b91c1c";
    const yellowText = "#92400e";
    const greenText = "#047857";

    let bgColor, textColor;
    if (score <= 50) {
      const factor = score / 50;
      bgColor = interpolateColor(redBg, yellowBg, factor);
      textColor = interpolateColor(redText, yellowText, factor);
    } else {
      const factor = (score - 50) / 50;
      bgColor = interpolateColor(yellowBg, greenBg, factor);
      textColor = interpolateColor(yellowText, greenText, factor);
    }
    return { bgColor, textColor };
  };

  // Build the course title—if a section exists, show it.
  const courseTitle = `${course.dept.toUpperCase()} ${course.number}` +
    (course.section ? ` (${course.section.toUpperCase()})` : " (Multiple Sections)");

  return (
    <div
      className={`fixed inset-y-0 right-0 w-80 bg-white shadow-xl border-l border-gray-200 z-50 transform transition-transform duration-300 ${
        isVisible ? "translate-x-0" : "translate-x-full"
      }`}
    >
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-bold">{courseTitle}</h2>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-800 text-2xl leading-none">
            &times;
          </button>
        </div>
        {/* Tab Navigation – now only “Details” and “Alternatives” */}
        <div className="flex-shrink-0 p-4 border-b border-gray-200">
          <div className="flex space-x-4">
            <button
              className={`pb-2 text-xs ${activeTab === "details" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-600"}`}
              onClick={() => setActiveTab("details")}
            >
              Details
            </button>
            <button
              className={`pb-2 text-xs ${activeTab === "alternatives" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-600"}`}
              onClick={() => setActiveTab("alternatives")}
            >
              Alternatives
            </button>
          </div>
        </div>
        {/* Tab Content */}
        <div className="flex-grow overflow-y-auto p-4">
          {activeTab === "details" && (
            <div className="text-sm">
              <p>
                <strong>Course:</strong> {courseTitle}
              </p>
              {detailsInfo ? (
                <div className="mt-2">
                  <p>
                    <strong>Title:</strong> {detailsInfo.title}
                  </p>
                  <p>
                    <strong>Units:</strong> {detailsInfo.units}
                  </p>
                  <p>
                    <strong>Description:</strong> {detailsInfo.description || "N/A"}
                  </p>
                </div>
              ) : (
                <p className="mt-2 text-gray-500">No details available.</p>
              )}
            </div>
          )}
          {activeTab === "alternatives" && (
            <div className="text-sm">
              <h3 className="font-semibold mb-2">Schedule Alternatives</h3>
              {alternatives.length > 0 ? (
                <ul className="space-y-2">
                  {alternatives.map((alt, index) => {
                    const altCourse = alt.combination.find(
                      opt => opt.dept === course.dept && opt.number === course.number
                    );
                    const colors = getColorsForScore(alt.score);
                    return (
                      <li key={index}>
                        <button
                          onClick={() => onSelectAlternative(alt)}
                          style={{ backgroundColor: colors.bgColor, color: colors.textColor }}
                          className="w-full text-left p-2 border rounded hover:shadow-md hover:scale-105 transform transition duration-200"
                        >
                          <p>Change to Section: {altCourse.section.toUpperCase()}</p>
                          <p className="text-xs">Score: {alt.score}</p>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-gray-500">No alternative schedule combinations available.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseSidebar;
